<script setup>
import {AmbientLight, Camera, GltfModel, Scene} from "troisjs";
import {ref} from "vue";
import * as THREE from "three";

const props = defineProps({
    object: String,
    rotateSpeed: Number,
    floatingSpeed: Number,
    angle: Number
})

const loaded = ref(false);
const rotateSpeedMultiplier = 50;
const rotateEaseDuration = 300;
const curRotateSpeed = ref(props.rotateSpeed)
const startRotateSpeed = ref(props.rotateSpeed)
const endRotateSpeed = ref(props.rotateSpeed)
const minRotateSpeed = ref(props.rotateSpeed)
const maxRotateSpeed = ref(props.rotateSpeed * rotateSpeedMultiplier)
const renderer = ref(null);
const camera = ref(null);
const light = ref(null);
const floatingSpeed = ref(props.floatingSpeed);
const startRotateTime = ref(null)

const fitCameraToObject = function ( camera, object, offset ) {
    offset = offset || 1.25;

    const boundingBox = new THREE.Box3();

    // get bounding box of object - this will be used to setup controls and camera
    boundingBox.setFromObject(object);

    const center = boundingBox.getCenter(new THREE.Vector3());

    const size = boundingBox.getSize(new THREE.Vector3());

    // get the max side of the bounding box (fits to width OR height as needed )
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = camera.fov * (Math.PI / 180);
    let cameraZ = Math.abs(maxDim / 4 * Math.tan(fov * 2));

    cameraZ *= offset; // zoom out a little so that objects don't fill the screen

    camera.position.z = cameraZ;

    const minZ = boundingBox.min.z;
    const cameraToFarEdge = (minZ < 0) ? -minZ + cameraZ : cameraZ - minZ;

    camera.far = cameraToFarEdge * 3;
    camera.updateProjectionMatrix();
    camera.lookAt(center)
}

const onLoad = (data) => {
    loaded.value = true;
    const scene = data.scene;
    const targetName = props.object;
    const allNames = scene.children.map(obj => obj.name);

    allNames.forEach(name => {
        if (name !== targetName) {
            scene.remove(scene.getObjectByName(name));
        }
    });

    const object = scene.getObjectByName(targetName)
    fitCameraToObject(camera.value.camera, object, 1.5);
    object.material.map.anisotropy = 16;
    renderer.value?.three?.renderer?.setPixelRatio( window.devicePixelRatio );

    renderer.value.onBeforeRender(() => {
        if (startRotateSpeed.value < endRotateSpeed.value) {
            curRotateSpeed.value = easeInQuad(
                Math.min(Date.now() - startRotateTime.value, rotateEaseDuration),
                startRotateSpeed.value,
                (endRotateSpeed.value - startRotateSpeed.value),
                rotateEaseDuration
            );
        } else {
            curRotateSpeed.value = easeOutQuad(
                Math.min(Date.now() - startRotateTime.value, rotateEaseDuration * 10),
                startRotateSpeed.value,
                (endRotateSpeed.value - startRotateSpeed.value),
                rotateEaseDuration * 10
            );
        }

        object.rotation.y += curRotateSpeed.value;

        if (!renderer.value.size.width) {
            const parent = renderer.value.three?.renderer?.domElement?.parentNode;

            if (parent && parent.clientWidth && parent.clientHeight) {
                renderer.value.setSize(parent?.clientWidth, parent.clientHeight);
            }
        }
    });
}

const onMouseOver = () => {
    startRotateTime.value = Date.now();
    startRotateSpeed.value = curRotateSpeed.value;
    endRotateSpeed.value = maxRotateSpeed.value;
}

const onMouseLeave = () => {
    startRotateTime.value = Date.now()
    startRotateSpeed.value = curRotateSpeed.value;
    endRotateSpeed.value = minRotateSpeed.value;
}

const onError = (data) => {
    console.log('onError', data);
}

function randomIntFromInterval(min, max) { // min and max included
    return Math.floor(Math.random() * (max - min + 1) + min)
}

function easeInQuad (t, b, c, d) {
    return c * (t /= d) * t + b;
}

function easeOutQuad (t, b, c, d) {
    return -c * (t /= d) * (t - 2) + b;
}
</script>

<template>
    <div :class="[`dice-wrapper w-full h-full`, loaded ? 'loaded' : '']"
         :style="`animation-delay: ${-randomIntFromInterval(0, floatingSpeed)}s; animation-duration: ${floatingSpeed}s;`"
         :data-dice="props.object">

        <div class="dice w-full h-full"
             :style="`transform: rotateZ(${props.angle}deg);`"
             @mouseover="onMouseOver"
             @mouseleave="onMouseLeave">
            <Renderer alpha resize antialias ref="renderer">
                <Camera ref="camera" :fov="50"/>
                <Scene>
                    <PointLight :position="{ y: 50, z: 50 }" :intensity="1" cast-shadow :shadow-map-size="{ width: 512, height: 512 }" ref="light" />
                    <AmbientLight :intensity="3"></AmbientLight>
                    <GltfModel :src="`/models/${props.object}.glb`"
                               @load="onLoad"
                               @error="onError"
                    />
                </Scene>
            </Renderer>
        </div>
    </div>
</template>
<style lang="scss">
    .screen-dice {
        &.active .dice-wrapper .dice {
            @apply opacity-100;
        }

        .dice-wrapper {
            &.loaded {
                animation: float 6s ease-in-out infinite;
            }

            .dice {
                @apply transition-opacity duration-300 opacity-0;
            }
        }
    }

    @keyframes float {
        0% {
            transform: translatey(0px);
        }

        50% {
            transform: translatey(-20px);
        }

        100% {
            transform: translatey(0px);
        }
    }
</style>
